import { useEffect, useMemo } from 'react';

import { useCrowdfundingProvider } from '#Context/index.js';
import { PeerTypes, useGetPeerPrizes } from '#api/index.js';
import { CampaignPrize } from '#api/new/types/prizes.js';
import { useShowCampaignSettings } from '@givebacks/api/store';
import { useForm } from '@givebacks/formy';
import { MultiSelect } from 'ui';
import { UseFormReturnType, isNotEmpty } from 'ui/Form';
import { IconChevronDown } from 'ui/Icons';

type SelectedPrizesFormValues = {
  prizes: string[];
};
type SelectedPrizesFormType = {
  selectedPrizes: UseFormReturnType<SelectedPrizesFormValues>;
};

const SelectedPrizesForm = ({
  peerType,
  initialPrizes,
  filteredPrizes,
}: {
  peerType: PeerTypes;
  initialPrizes?: string[];
  filteredPrizes?: CampaignPrize[];
}) => {
  const { campaignId, causeId } = useCrowdfundingProvider();
  const { data: campaignSettings } = useShowCampaignSettings({
    causeId,
    campaignId,
  });

  const { data: prizes } = useGetPeerPrizes({
    causeId,
    campaignId,
    peerType,
  });

  const prizeData = useMemo(() => {
    const filteredTopPrizes = filteredPrizes
      ? filteredPrizes.filter((prize) => !prize.type.includes('Top'))
      : prizes?.filter((prize) => !prize.type.includes('Top'));

    // If campaign is not read-only, use the filtered prizes
    const prizeData = !campaignSettings?.isCampaignReadOnly ? filteredTopPrizes : prizes;

    return prizeData?.map((prize) => ({
      label: prize.title,
      value: prize.uuid,
    }));
  }, [filteredPrizes, prizes, campaignSettings?.isCampaignReadOnly]);

  const showMorePrizes = useMemo(() => {
    return prizeData && prizeData.length > 1;
  }, [prizeData]);

  const form = useForm<SelectedPrizesFormValues>(
    {
      initialValues: {
        prizes: [],
      },
      validate: {
        prizes: isNotEmpty('Prizes are required'),
      },
    },
    {
      id: 'selectedPrizes',
    }
  );

  useEffect(() => {
    if (initialPrizes) {
      form.initialize({
        prizes: initialPrizes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- If prize data changes and we don't have a default value- initialize the form
  }, [initialPrizes]);

  return (
    <MultiSelect
      data={prizeData}
      label="Prizes"
      placeholder={'Select prizes'}
      searchable
      clearable
      rightSection={showMorePrizes ? <IconChevronDown size={16} brand="tabler" /> : undefined}
      {...form.getInputProps('prizes')}
    />
  );
};

export { SelectedPrizesForm };
export type { SelectedPrizesFormType };
